import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Checkbox } from 'antd';
import agreementData from './RegisterAgreement.json';

const RegisterConsentModel = ({ visible, onComplete, onCancel }) => {
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [agreementTypes, setAgreementTypes] = useState([]);
    const [currentAgreement, setCurrentAgreement] = useState(null);
    const [isAgreed, setIsAgreed] = useState(false);
    const [completedAgreements, setCompletedAgreements] = useState([]);
    const contentRef = useRef(null);

    useEffect(() => {
        // Extract all agreement types from JSON (privacyPolicy, termsOfService, etc.)
        const types = Object.keys(agreementData).filter(key => agreementData[key]);
        setAgreementTypes(types);
        
        if (types.length > 0) {
            setCurrentAgreement(agreementData[types[0]]);
        }
        
    }, []);

    useEffect(() => {
        // Reset everything when modal opens/closes
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        if (visible) {
            // Reset to first step when modal opens
            setCurrentSectionIndex(0);
            setIsAgreed(false);
            setCompletedAgreements([]);
            
            // Set to first agreement
            if (agreementTypes.length > 0) {
                setCurrentAgreement(agreementData[agreementTypes[0]]);
            }
        }
    }, [visible, agreementTypes]);

    useEffect(() => {
        // Reset checkbox when moving to next agreement
        setIsAgreed(false);
    }, [currentSectionIndex]);

    const handleAgree = () => {
        if (!isAgreed) {
            return; // Don't proceed if checkbox isn't checked
        }

        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        const currentType = agreementTypes[currentSectionIndex];
        const newCompleted = [...completedAgreements, currentType];
        setCompletedAgreements(newCompleted);

        // Check if there are more agreements to show
        if (currentSectionIndex + 1 < agreementTypes.length) {
            // Move to next agreement
            const nextIndex = currentSectionIndex + 1;
            setCurrentSectionIndex(nextIndex);
            setCurrentAgreement(agreementData[agreementTypes[nextIndex]]);
        } else {
            // All agreements completed
            onComplete(newCompleted);
        }
    };

    const handleDecline = () => {
        onCancel();
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    };

    const handleCheckboxChange = (e) => {
        setIsAgreed(e.target.checked);
    };

    if (!currentAgreement || agreementTypes.length === 0) {
        return null;
    }

    return (
        <Modal
            title={
                <div className="consent-modal-header">
                    <h2>{currentAgreement.title}</h2>
                    <p className="consent-progress">
                        Step {currentSectionIndex + 1} of {agreementTypes.length}
                    </p>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={null}
            className="consent-modal"
        >
            <div className="consent-content" ref={contentRef}>
                <div className="consent-date">
                    Effective Date: {currentAgreement.effectiveDate}
                </div>
                
                <div className="consent-sections">
                    {currentAgreement.sections.map((section) => (
                        <div key={section.id} className="consent-section">
                            <h3>{section.title}</h3>
                            <p>{section.content}</p>
                            
                            {section.details && (
                                <ul>
                                    {section.details.map((detail, index) => (
                                        <li key={index}>{detail}</li>
                                    ))}
                                </ul>
                            )}
                            
                            {section.note && (
                                <p className="section-note">{section.note}</p>
                            )}
                            
                            {section.email && (
                                <p>
                                    <a href={`mailto:${section.email}`}>{section.email}</a>
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                <div className="consent-agreement-section">
                    <Checkbox 
                        checked={isAgreed} 
                        onChange={handleCheckboxChange}
                        className="consent-checkbox"
                    >
                        I have read and agree to the {currentAgreement.title}
                    </Checkbox>
                </div>

                <div className="consent-buttons">
                    <Button 
                        onClick={handleDecline} 
                        size="large"
                        className="decline-button"
                    >
                        Decline
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleAgree} 
                        disabled={!isAgreed}
                        size="large"
                        className="agree-button"
                    >
                        {currentSectionIndex + 1 < agreementTypes.length ? 'Agree & Continue' : 'Agree & Complete'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RegisterConsentModel;